import { makeStyles } from "$components/mui-styles";
import { Paper, type PaperProps } from "@material-ui/core";
import classnames from "classnames";
import React from "react";
import { CardContext } from "./context";
import { CardColors } from "./types";
import { getCardColor } from "./utils";

const useStyles = makeStyles((theme) => ({
  root: ({ color, expandable }: { color: CardColors; expandable: boolean }) => {
    const borderLeftColor = getCardColor(theme, color);
    return {
      borderLeft: `0.5rem solid ${borderLeftColor}`,
      overflow: "hidden",
      // An expandable card delegates padding to its body sections (summary,
      // details, footer) so the footer can sit full-bleed; a plain card owns
      // the responsive padding itself.
      ...(expandable
        ? { padding: 0 }
        : {
            padding: theme.spacing(1),
            [theme.breakpoints.up("sm")]: {
              padding: theme.spacing(2),
            },
          }),
    };
  },
}));

export const Card: FC<
  Omit<PaperProps, "children"> & {
    children: React.ReactNode;
    color?: CardColors;
    expandable?: boolean;
    defaultExpanded?: boolean;
    expanded?: boolean;
    disabled?: boolean;
    hideExpandIcon?: boolean;
    className?: string;
    classes?: {
      root?: string;
    };
  }
> = ({
  children,
  color = CardColors.TRANSPARENT,
  expandable,
  defaultExpanded,
  expanded,
  disabled,
  hideExpandIcon,
  className,
  classes: overrideClasses,
  ...props
}) => {
  const classes = {
    ...useStyles({ color, expandable: !!expandable }),
    ...overrideClasses,
  };

  return (
    <Paper
      elevation={2}
      className={classnames(classes?.root, className)}
      {...props}
    >
      <CardContext.Provider
        value={{
          defaultExpanded: !!defaultExpanded,
          expandable: !!expandable,
          expanded,
          disabled,
          hideExpandIcon,
        }}
      >
        {children}
      </CardContext.Provider>
    </Paper>
  );
};
